import { Box, Stack, Typography } from '@mui/material'
import IconTruck from '@planning/components/icon/IconTruck'
import IconVessel from '@planning/components/icon/IconVessel'
import { VisitDataHeaderAttributesCard } from '@planning/pages/VesselVisit/Components/molecules/VisitDataHeaderAttributesCard'
import { useTranslate } from '@tolgee/react'
import { CarrierType } from '@tom-ui/integration'
import { TrainIcon, useMinimalsTheme } from '@tom-ui/ui'
import { computed } from 'mobx'
import { observer } from 'mobx-react-lite'
import { ReleaseStatus } from '../../models/ReleaseStatus'
import { ReleaseStatusChip } from '../atoms/ReleaseStatusChip'
import { ReleaseValidityDate } from '../atoms/ReleaseValidityDate'
import { ContainerRequirements, ContainerRequirementsStack } from './ContainerRequirementsStack'

type Props = {
  carrierType: CarrierType
  carrierName: string
  validityDate?: string | null | undefined
  status: ReleaseStatus
  containerRequirements?: ContainerRequirements
  amounts: {
    total?: number | null | undefined
    assigned: number
    fulfilled: number
  }
  referenceNumber?: string
  vesselNumber?: string
}

export const ReleaseSummary = observer((props: Props) => {
  const {
    carrierType,
    carrierName,
    validityDate,
    status,
    containerRequirements,
    amounts,
    referenceNumber,
    vesselNumber,
  } = props
  const { t } = useTranslate()
  const theme = useMinimalsTheme()

  const amountAttributes = computed(() => {
    const attributes = [
      {
        label: t('assigned', 'Assigned'),
        value: amounts.assigned,
      },
      {
        label: t('fulfilled', 'Fulfilled'),
        value: amounts.fulfilled,
      },
    ]

    if (amounts.total !== null && amounts.total !== undefined) {
      attributes.unshift({
        label: t('total', 'Total'),
        value: amounts.total,
      })
    }

    return attributes
  }).get()

  return (
    <Box padding={theme.customSpacing.m} bgcolor={theme.palette.common.white}>
      <Stack direction='row' spacing={4} alignItems='center' justifyContent='space-between'>
        <Stack spacing={theme.customSpacing.xxs}>
          <Stack spacing={theme.customSpacing.xxs} direction='row' alignItems='center'>
            {carrierType === CarrierType.Vessel && <IconVessel />}
            {carrierType === CarrierType.Train && <TrainIcon />}
            {carrierType === CarrierType.Truck && <IconTruck />}

            <Typography variant='subtitle1' align='center'>
              {carrierName} {vesselNumber && `(${vesselNumber})`}
            </Typography>
          </Stack>

          {referenceNumber && (
            <Typography variant='body2' sx={{ color: 'text.secondary', fontWeight: 'bold' }}>
              {t('reference', 'Reference')}: {referenceNumber}
            </Typography>
          )}

          <Stack direction='row' spacing={theme.customSpacing.xxs} alignItems='center'>
            <Typography variant='body2' color='secondary'>
              {t('validityDate', 'Validity date')}:
            </Typography>
            <Typography variant='body2' color='secondary'>
              <ReleaseValidityDate validityDate={validityDate} />
            </Typography>
          </Stack>
          <ReleaseStatusChip status={status} />
        </Stack>
        <Stack spacing={theme.customSpacing.s} direction='row'>
          {containerRequirements && (
            <ContainerRequirementsStack containerRequirements={containerRequirements} />
          )}

          <VisitDataHeaderAttributesCard
            title={t('amount', 'Amount')}
            attributes={amountAttributes}
          />
        </Stack>
      </Stack>
    </Box>
  )
})
